"use client";

import { useEffect, useMemo, useRef, useState, useTransition } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Upload, FileCheck, BookmarkPlus } from "lucide-react";
import {
  submitPayoutForApprovalAction,
  uploadDocumentAction,
  payoutRequirementsAction,
  createSavedRecipientAction,
  type PayoutRequirements,
} from "@/app/actions";
import { CHAINS, FIAT, STABLECOINS, fmtAmount } from "@/lib/format";
import { CurrencySelect } from "@/components/currency";
import { CountrySelect } from "@/components/country";

/** A reusable local recipient (this app's address book, not a Conduit object). */
export type SavedRecipientOption = {
  id: number;
  label: string;
  kind: "fiat" | "crypto";
  destCountry?: string | null;
  recipientType?: string | null;
  chain?: string | null;
  address?: string | null;
  custody?: string | null;
  values?: Record<string, string> | null;
};

/** The subset of a Conduit whitelist recipient this form needs to auto-fill from. */
export type WhitelistRecipientOption = {
  id: string;
  legalName: string;
  rail: "us" | "swift";
  status: string;
  accountNumber?: string | null;
  routingNumber?: string | null;
  bic?: string | null;
  iban?: string | null;
};

const PURPOSES = [
  "payment_for_goods_or_services",
  "treasury_management",
  "prefunding",
  "payroll",
  "investments",
  "intercompany",
  "other",
];

// Prefunding is rail-gated by Conduit at business-logic level — the schema
// accepts it everywhere, but crypto payouts 422 with
// PREFUNDING_UNSUPPORTED_RAIL (verified live 2026-07-22; fedwire works).
const purposesFor = (kind: "fiat" | "crypto") =>
  kind === "crypto" ? PURPOSES.filter((p) => p !== "prefunding") : PURPOSES;

// Conduit's enum values are fixed — these are display-only labels/hints to
// stop "treasury management" and "intercompany" from reading the same way.
const PURPOSE_META: Record<string, { label: string; hint?: string; docHint?: string }> = {
  payment_for_goods_or_services: {
    label: "Payment for goods or services",
    docHint: "the invoice or contract this payment settles",
  },
  treasury_management: {
    label: "Treasury management (own account)",
    hint: "Moving your own funds for liquidity or cash management — a different bank, not a different company.",
    docHint:
      "a bank verification letter for the receiving account — Conduit's accepted list is the same for every purpose, but there's rarely an invoice for an internal sweep, so this (or 'other') is the natural fit",
  },
  prefunding: {
    label: "Prefunding",
    hint: "Topping up your own downstream account ahead of use — not a payment to a counterparty.",
    docHint:
      "a bank verification letter for the receiving account — same documentation as treasury management, since there's no invoice for a prefunding transfer",
  },
  payroll: { label: "Payroll", docHint: "your payroll register for this period" },
  investments: { label: "Investments", docHint: "the investment or subscription agreement" },
  intercompany: {
    label: "Intercompany transfer (related entity)",
    hint: "Sending to a different legal entity in your own corporate group. Requires a whitelisted recipient.",
  },
  other: { label: "Other", docHint: "whatever best evidences this payment" },
};

/** A funding source the customer can pay from. */
export type PayoutSource = {
  value: string;
  label: string;
  kind: "auto" | "virtual_account" | "wallet";
  id?: string;
  code?: string;
  chain?: string;
  /** Current available balance in `code` — drives the "wait for funds" offer (undefined = unknown). */
  available?: number;
};

type ReqField = NonNullable<PayoutRequirements["fields"]>[number];

/** Dotted requirement name → { destination: {...} } payload subtree. */
function setDeep(obj: Record<string, unknown>, path: string[], value: unknown) {
  let cur = obj;
  for (let i = 0; i < path.length - 1; i++) {
    cur[path[i]] = cur[path[i]] ?? {};
    cur = cur[path[i]] as Record<string, unknown>;
  }
  cur[path[path.length - 1]] = value;
}

/** Human labels for API field names where auto-humanizing reads wrong. */
const LABEL_OVERRIDES: Record<string, string> = {
  bic: "SWIFT / BIC code",
  iban: "IBAN",
  accountNumber: "Account number",
  routingNumber: "Routing number (ABA)",
  accountType: "Account type",
  legalName: "Legal name",
  bankName: "Bank name",
  countryOfRegistration: "Country of registration",
  purposeOfPayment: "Purpose of payment",
  reference: "Payment reference",
  description: "Payment description",
  authorizationType: "Authorization type",
  addressLine1: "Address line 1",
  addressLine2: "Address line 2",
  postalCode: "Postal code",
};

function fieldLabel(name: string) {
  const last = name.split(".").pop() ?? name;
  if (LABEL_OVERRIDES[last]) return LABEL_OVERRIDES[last];
  const words = last.replace(/([A-Z])/g, " $1").toLowerCase().trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

const BANK_FIELDS = new Set(["accountNumber", "routingNumber", "accountType", "iban", "bic", "bankName"]);

function fieldGroup(name: string): string {
  if (name.includes(".bankAddress.")) return "bank address";
  if (name.includes(".postalAddress.")) return "recipient address";
  if (name.startsWith("destination.recipient.")) {
    const leaf = name.split(".").pop() ?? "";
    return BANK_FIELDS.has(leaf) ? "bank details" : "recipient";
  }
  return "other";
}

const GROUP_ORDER = ["recipient", "recipient address", "bank details", "bank address", "other"];

/** Seed values from the XE-style amount step (PayoutStart), if the caller used one. */
export type PayoutInitialValues = {
  kind: "fiat" | "crypto";
  code: string;
  amount: string;
  sourceValue?: string;
  chain?: string;
  /** Which side `amount` is denominated in — defaults to "destination" (recipient gets exactly this much). */
  lockSide?: "source" | "destination";
  /** Unfunded cross-currency payout — pre-checks the wait-for-funds option in the form. */
  autoExecute?: boolean;
};

/** Recipient/bank fields read off an uploaded invoice by the screener, applied once requirements load. */
export type PayoutPrefill = {
  destCountry?: string;
  recipientType?: string;
  purpose?: string;
  values?: Record<string, string>;
  docs?: Array<{ id: string; fileName: string }>;
  warnings?: string[];
};

export default function PayoutForm({
  customers,
  defaultCustomerId,
  sources = [],
  actingUser,
  savedRecipients = [],
  whitelistRecipients = [],
  initial,
  prefill,
  initialRecipientChoice,
  onEditAmount,
}: {
  customers: Array<{ id: string; name: string }>;
  defaultCustomerId?: string;
  sources?: PayoutSource[];
  actingUser?: { email: string; role: string } | null;
  savedRecipients?: SavedRecipientOption[];
  whitelistRecipients?: WhitelistRecipientOption[];
  /** Pre-fills kind/currency/amount/source from a prior XE-style amount step. */
  initial?: PayoutInitialValues;
  /** Recipient/bank fields (and the invoice itself, as the supporting document) read by the invoice screener. */
  prefill?: PayoutPrefill;
  /** "saved:<id>" or "wl:<id>" — pre-selects the Recipient dropdown, deep-linked from the Saved/Whitelisted recipients pages. */
  initialRecipientChoice?: string;
  /** Shows a "change amount" link back to the amount step, if one preceded this form. */
  onEditAmount?: () => void;
}) {
  const router = useRouter();
  const [pending, start] = useTransition();
  const [error, setError] = useState<string | null>(null);
  const [submittedId, setSubmittedId] = useState<number | null>(null);
  const [docs, setDocs] = useState<Array<{ id: string; fileName: string }>>([]);
  const mayInitiate = !!actingUser && (actingUser.role === "initiator" || actingUser.role === "admin");

  const [f, setF] = useState({
    customerId: defaultCustomerId ?? customers[0]?.id ?? "",
    sourceValue: initial?.sourceValue ?? "",
    kind: initial?.kind ?? ("fiat" as "fiat" | "crypto"),
    code: initial?.code ?? "USD",
    chain: initial?.chain ?? "base",
    amount: initial?.amount ?? "",
    lockSide: initial?.lockSide ?? ("destination" as "source" | "destination"),
    purpose: "payment_for_goods_or_services",
    recipientType: "business",
    // crypto destination
    address: "",
    custody: "third_party",
    clientReferenceId: "",
    // Unfunded cross-currency payouts: create the order without balance and
    // let Conduit's sweep execute it once the source covers totalDebit.
    autoExecute: initial?.autoExecute ?? false,
  });
  const [destCountry, setDestCountry] = useState("US");
  const [values, setValues] = useState<Record<string, string>>({});
  const [reqs, setReqs] = useState<PayoutRequirements | null>(null);
  const [reqLoading, setReqLoading] = useState(false);

  // Recipient picker: "new" | "saved:<id>" | "wl:<id>". Selecting a saved or
  // whitelisted recipient stashes its field values here; the requirements
  // effect applies them once the fresh (country/rail-specific) field set loads.
  const [recipientChoice, setRecipientChoice] = useState("new");
  const [recipientLabel, setRecipientLabel] = useState("");
  const [savingRecipient, setSavingRecipient] = useState(false);
  const [savedNotice, setSavedNotice] = useState<string | null>(null);
  const pendingValuesRef = useRef<Record<string, string> | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);
  // True once the user has actually picked an authorizationType themselves —
  // only then does the purpose-based suggestion stop refreshing on change.
  const [achTouched, setAchTouched] = useState(false);

  function set<K extends keyof typeof f>(k: K, v: (typeof f)[K]) {
    setF((x) => ({ ...x, [k]: v }));
  }

  function applyRecipient(choice: string) {
    setRecipientChoice(choice);
    setSavedNotice(null);
    if (choice === "new") return;

    if (choice.startsWith("saved:")) {
      const id = Number(choice.slice("saved:".length));
      const r = savedRecipients.find((x) => x.id === id);
      if (!r) return;
      if (r.kind === "crypto") {
        setF((x) => ({
          ...x,
          kind: "crypto",
          // Same guard as the fiat/crypto toggle: prefunding can't ride a crypto rail.
          purpose: purposesFor("crypto").includes(x.purpose) ? x.purpose : "payment_for_goods_or_services",
          chain: r.chain ?? x.chain,
          address: r.address ?? x.address,
          custody: r.custody ?? x.custody,
        }));
        return;
      }
      set("recipientType", r.recipientType ?? "business");
      setDestCountry(r.destCountry ?? "US");
      pendingValuesRef.current = r.values ?? {};
      setRefreshTick((t) => t + 1);
      return;
    }

    if (choice.startsWith("wl:")) {
      const id = choice.slice("wl:".length);
      const wr = whitelistRecipients.find((x) => x.id === id);
      if (!wr) return;
      setF((x) => ({ ...x, kind: "fiat", purpose: "intercompany", recipientType: "business" }));
      // IBAN's first two characters are its ISO country code; a BIC's 5th-6th
      // characters are too (ISO 9362) — used when the recipient was
      // registered by BIC + account number instead, which has no IBAN to read.
      const country = wr.iban
        ? wr.iban.slice(0, 2).toUpperCase()
        : wr.bic && wr.bic.length >= 6
          ? wr.bic.slice(4, 6).toUpperCase()
          : "US";
      setDestCountry(wr.rail === "us" ? "US" : country);
      pendingValuesRef.current = {
        "destination.recipient.legalName": wr.legalName,
        ...(wr.rail === "us"
          ? {
              "destination.recipient.accountNumber": wr.accountNumber ?? "",
              "destination.recipient.routingNumber": wr.routingNumber ?? "",
            }
          : {
              "destination.recipient.bic": wr.bic ?? "",
              ...(wr.iban
                ? { "destination.recipient.iban": wr.iban }
                : { "destination.recipient.accountNumber": wr.accountNumber ?? "" }),
            }),
      };
      setRefreshTick((t) => t + 1);
      return;
    }
  }

  // Apply the invoice screener's extracted fields the same way a saved/whitelisted
  // recipient pick does — stash them and let the requirements effect below merge
  // them in once the country/rail-specific field set has loaded. Guarded by a
  // ref (not just the [] dep array) because React 18 dev Strict Mode
  // double-invokes mount effects, which would otherwise attach the invoice's
  // supporting document twice.
  const appliedPrefillRef = useRef(false);
  useEffect(() => {
    if (!prefill || appliedPrefillRef.current) return;
    appliedPrefillRef.current = true;
    if (prefill.destCountry) setDestCountry(prefill.destCountry);
    if (prefill.recipientType) set("recipientType", prefill.recipientType);
    if (prefill.purpose) set("purpose", prefill.purpose);
    if (prefill.values) pendingValuesRef.current = prefill.values;
    if (prefill.docs?.length) setDocs((d) => [...d, ...prefill.docs!]);
    setRefreshTick((t) => t + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Deep-linked from the Saved/Whitelisted recipients pages' "Pay" buttons —
  // apply the same way a manual dropdown pick does, once, on mount.
  const appliedRecipientRef = useRef(false);
  useEffect(() => {
    if (!initialRecipientChoice || appliedRecipientRef.current) return;
    appliedRecipientRef.current = true;
    applyRecipient(initialRecipientChoice);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const recipientOptions = savedRecipients.filter((r) => r.kind === f.kind);

  // Pre-select the source that already holds the payout currency; the user can override.
  const autoPick = sources.find((s) => s.code === f.code)?.value ?? sources[0]?.value ?? "";
  const effectiveSourceValue = f.sourceValue || autoPick;
  const source = sources.find((s) => s.value === effectiveSourceValue);
  const crossCurrency = !!source?.code && source.code !== f.code;
  // "Wait for funds" is only offered when the source can't cover the amount —
  // funded payouts settle instantly. ~1:1 USD↔stablecoin pairs make the typed
  // amount comparable against the source balance on either lock side.
  const insufficient = !!source && !!f.amount && source.available !== undefined && Number(f.amount) > source.available;

  // Domestic (US) moves over Fedwire; anywhere else goes SWIFT.
  const derivedRail = destCountry === "US" ? "fedwire" : "swift";

  // Requirements drive the fiat field set.
  useEffect(() => {
    if (f.kind !== "fiat" || !destCountry) return;
    let alive = true;
    setReqLoading(true);
    payoutRequirementsAction({
      purpose: f.purpose,
      rail: derivedRail,
      recipientType: f.recipientType,
      destinationCountry: destCountry,
    })
      .then((res) => {
        if (!alive) return;
        if (res.ok) {
          // Conduit's schema marks the recipient's postal code optional for
          // some rails/countries, but we always want it on file for payouts.
          setReqs({
            ...res.data,
            fields: res.data.fields?.map((fd) =>
              fd.name.includes(".postalAddress.") && fd.name.endsWith(".postalCode") ? { ...fd, required: true } : fd,
            ),
          });
          const pending = pendingValuesRef.current;
          pendingValuesRef.current = null;
          const achField = res.data.fields?.find((fd) => fd.name === "destination.ach.authorizationType");
          // Default the NACHA authorization type by purpose rather than leaving
          // it blank — this is a suggestion to review, not something forced:
          // it stops refreshing the moment the user picks a value themselves
          // (achTouched), tracked as real state, not a side effect inside the
          // updater below (React 18 dev double-invokes updaters to catch
          // exactly that kind of impurity).
          const achDefault: Record<string, string> =
            achField && !achTouched ? { "destination.ach.authorizationType": f.purpose === "payroll" ? "written" : "corporate_agreement" } : {};
          setValues((v) => ({
            ...v,
            ...achDefault,
            // a recipient just picked from the dropdown wins over anything above
            ...(pending ?? {}),
          }));
        } else {
          setError(res.error);
        }
      })
      .finally(() => {
        if (alive) setReqLoading(false);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // achTouched deliberately excluded: it should stop future auto-defaults
    // (read via closure when purpose/country next change), not trigger an
    // extra requirements re-fetch the instant the user picks a value.
  }, [f.kind, f.purpose, f.recipientType, destCountry, derivedRail, refreshTick]);

  const visibleFields = useMemo(
    () =>
      (reqs?.fields ?? []).filter(
        (fd) =>
          !(fd.enum && fd.enum.length === 1) &&
          // Already sent as-is from the "Pay from" selection (source.id) in
          // submit() below — showing it as a free-text field would let a user
          // type a value that's silently discarded, while blocking submit
          // until they type *something*.
          fd.name !== "virtualAccountId",
      ),
    [reqs],
  );
  const grouped = useMemo(() => {
    const g = new Map<string, ReqField[]>();
    for (const fd of visibleFields) {
      const key = fieldGroup(fd.name);
      g.set(key, [...(g.get(key) ?? []), fd]);
    }
    return GROUP_ORDER.filter((k) => g.has(k)).map((k) => [k, g.get(k)!] as const);
  }, [visibleFields]);

  const missingRequired = visibleFields.filter((fd) => fd.required && !values[fd.name]).length;
  // Crypto payouts need supporting documents too (422 DOCUMENTATION_REQUIRED
  // otherwise, verified live) — there's just no requirements response to read
  // the flag from, so fall back to the same non-intercompany rule.
  const needsDocs =
    f.kind === "fiat"
      ? (reqs?.documentation?.required ?? f.purpose !== "intercompany")
      : f.purpose !== "intercompany";

  async function onUpload(files: FileList | null) {
    if (!files) return;
    for (const file of Array.from(files)) {
      const fd = new FormData();
      fd.set("file", file);
      fd.set("purpose", "transaction_support");
      fd.set("name", file.name);
      const res = await uploadDocumentAction(fd);
      if (res.ok) setDocs((d) => [...d, { id: res.data.id, fileName: res.data.fileName ?? file.name }]);
      else setError(`${file.name}: ${res.error}`);
    }
  }

  const canSaveRecipient =
    f.kind === "crypto" ? !!f.address : !!values["destination.recipient.legalName"];

  function saveRecipient() {
    setSavingRecipient(true);
    setSavedNotice(null);
    const label = recipientLabel.trim();
    const payload =
      f.kind === "crypto"
        ? { customerId: f.customerId, label, kind: "crypto" as const, chain: f.chain, address: f.address, custody: f.custody }
        : { customerId: f.customerId, label, kind: "fiat" as const, destCountry, recipientType: f.recipientType, values };
    createSavedRecipientAction(payload).then((res) => {
      setSavingRecipient(false);
      if (res.ok) {
        setSavedNotice(`Saved — "${label}" now appears in the Recipient dropdown above.`);
        setRecipientLabel("");
        router.refresh();
      } else {
        setSavedNotice(null);
        setError(res.error);
      }
    });
  }

  function destinationFields(): Record<string, unknown> {
    if (f.kind === "crypto") {
      // Payout schema nests crypto details under recipient, and custody is an
      // attestation object — a flat {rail, chain, address, custody} 400s.
      return {
        type: "crypto",
        recipient: {
          rail: "crypto",
          chain: f.chain,
          address: f.address,
          attestation: { custody: f.custody },
        },
      };
    }
    const root: Record<string, unknown> = {};
    for (const fd of reqs?.fields ?? []) {
      const fixed = fd.enum && fd.enum.length === 1 ? fd.enum[0] : undefined;
      const v = fixed ?? values[fd.name];
      if (v === undefined || v === "") continue;
      setDeep(root, fd.name.split("."), v);
    }
    return (root.destination as Record<string, unknown>) ?? {};
  }

  /**
   * `POST /orders`' autoPayout is a flat object (rail/recipient/ach/purpose/
   * documents directly under autoPayout) — no `enabled`/`destination`
   * wrapper and no `type` key, unlike the standalone payout `destination`
   * shape destinationFields() otherwise matches. It also needs its own
   * purpose + documents, same as a standalone payout would.
   */
  function autoPayoutFields(): Record<string, unknown> {
    const { type: _type, ...rest } = destinationFields();
    return {
      ...rest,
      purpose: f.purpose,
      ...(docs.length ? { documents: docs.map((d) => d.id) } : {}),
    };
  }

  /** Every payout goes to the approval queue — a different person releases it. */
  function submit() {
    setError(null);
    const recipient =
      f.kind === "crypto"
        ? f.address
        : (values["destination.recipient.legalName"] ?? "recipient");
    // f.amount is denominated in whichever side f.lockSide points at.
    const amountLabel = crossCurrency && f.lockSide === "source" ? `${f.amount} ${source?.code}` : `${f.amount} ${f.code}`;
    const summary = crossCurrency
      ? `${amountLabel} to ${recipient} · funded from ${source?.code} (${source?.label ?? ""})`
      : `${amountLabel} to ${recipient}`;

    let kind: "payout" | "order" = "payout";
    let body: Record<string, unknown>;

    if (crossCurrency) {
      if (!source) return;
      kind = "order";
      if (f.kind === "fiat") {
        const va = sources.find((s) => s.kind === "virtual_account" && s.code === f.code);
        if (!va) {
          setError(`A ${f.code} virtual account is required to settle ${source.code} → ${f.code} payouts. Provision one on the Account page.`);
          return;
        }
        body = {
          // Wallet order sides must name the asset — the wallet id alone is
          // ambiguous (one chain can hold USDC and USDT side by side).
          source: { type: "wallet", id: source.id, asset: { code: source.code, chain: source.chain } },
          destination: { type: "virtual_account", id: va.id },
          lockSide: f.lockSide,
          amount: f.amount,
          ...(f.autoExecute && insufficient ? { autoExecute: true } : {}),
          autoPayout: autoPayoutFields(),
        };
      } else {
        const w = sources.find((s) => s.kind === "wallet" && s.chain === f.chain);
        if (!w) {
          setError(`A ${f.chain} wallet is required to settle ${source?.code} → ${f.code} payouts. Create one on the Account page.`);
          return;
        }
        body = {
          source: { type: "virtual_account", id: source.id },
          // The receiving asset must be explicit too — "the base wallet" could
          // take this payout in any stablecoin it supports.
          destination: { type: "wallet", id: w.id, asset: { code: f.code, chain: f.chain } },
          lockSide: f.lockSide,
          amount: f.amount,
          ...(f.autoExecute && insufficient ? { autoExecute: true } : {}),
          autoPayout: autoPayoutFields(),
        };
      }
    } else {
      // Fiat payouts must name which virtual account funds them — the API
      // requires virtualAccountId even though not every requirements
      // response mentions it.
      if (f.kind === "fiat" && source?.kind !== "virtual_account") {
        setError(`A ${f.code} virtual account is required to fund this payout. Provision one on the Account page.`);
        return;
      }
      body = {
        customerId: f.customerId,
        ...(f.kind === "fiat" ? { virtualAccountId: source!.id } : {}),
        assetAmount: {
          code: f.code,
          ...(f.kind === "crypto" ? { chain: f.chain } : {}),
          amount: f.amount,
        },
        destination: destinationFields(),
        purpose: f.purpose,
        ...(docs.length ? { documents: docs.map((d) => d.id) } : {}),
        // Conduit only accepts [A-Za-z0-9_-:.] — sanitize free text instead of
        // rejecting it, since users naturally type spaces/punctuation here.
        ...(f.clientReferenceId
          ? { clientReferenceId: f.clientReferenceId.trim().replace(/[^A-Za-z0-9_\-:.]/g, "-") }
          : {}),
      };
    }

    start(async () => {
      const res = await submitPayoutForApprovalAction({
        customerId: f.customerId,
        kind,
        body,
        summary,
      });
      if (res.ok) {
        setSubmittedId(res.data.id);
        router.refresh();
      } else setError(res.error);
    });
  }

  const codes = f.kind === "crypto" ? STABLECOINS : FIAT;

  function renderReqField(fd: ReqField) {
    const name = fd.name;
    const v = values[name] ?? "";
    const leaf = name.split(".").pop() ?? "";
    const isCountry = leaf === "country" || leaf === "countryOfRegistration";

    let control: React.ReactNode;
    if (isCountry) {
      control = <CountrySelect value={v} onChange={(c) => setValues((x) => ({ ...x, [name]: c }))} />;
    } else if (fd.enum && fd.enum.length > 1) {
      control = (
        <select
          className="field"
          value={v}
          onChange={(e) => {
            if (name === "destination.ach.authorizationType") setAchTouched(true);
            setValues((x) => ({ ...x, [name]: e.target.value }));
          }}
        >
          <option value="">Select…</option>
          {fd.enum.map((o) => (
            <option key={o} value={o}>
              {o.replaceAll("_", " ").toLowerCase()}
            </option>
          ))}
        </select>
      );
    } else {
      control = (
        <input
          className={`field${["iban", "bic", "accountNumber", "routingNumber"].includes(leaf) ? " mono" : ""}`}
          type={leaf.toLowerCase().includes("phone") ? "tel" : "text"}
          value={v}
          onChange={(e) => setValues((x) => ({ ...x, [name]: e.target.value }))}
        />
      );
    }

    return (
      <div key={name}>
        <label className="label">
          {fieldLabel(name)}
          {fd.required ? "" : " (optional)"}
        </label>
        {control}
        {name === "destination.ach.authorizationType" && (
          <p className="mt-1 text-[12px] font-light" style={{ color: "var(--c-ink-soft)" }}>
            How this specific payment was authorized (a NACHA classification) — not which app you're using.
            Pre-filled from the purpose; change it if a signed form or web consent applies instead.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="card rise p-8">
      {prefill && (
        <div className="card-cream mb-6 p-4 text-[14px] font-light">
          Prefilled from the uploaded invoice — double-check the fields below before submitting.
          {prefill.warnings && prefill.warnings.length > 0 && (
            <ul className="mt-2 list-disc pl-5 text-[13px]" style={{ color: "var(--c-danger)" }}>
              {prefill.warnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {onEditAmount && (
        <button type="button" className="mb-6 flex items-center gap-2 text-[13px] font-medium underline-offset-2 hover:underline" onClick={onEditAmount}>
          {f.lockSide === "source"
            ? `${fmtAmount(f.amount)} ${source?.code ?? f.code} → ${f.code}`
            : `${source?.code ?? f.code} → ${fmtAmount(f.amount)} ${f.code}`}
          <span style={{ color: "var(--c-ink-soft)" }}>· change amount</span>
        </button>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {customers.length > 1 && (
          <div className="col-span-full">
            <label className="label">Customer</label>
            <select className="field" value={f.customerId} onChange={(e) => set("customerId", e.target.value)}>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="col-span-full">
          <label className="label">Pay from</label>
          {sources.length === 0 ? (
            <p className="text-[13px] font-light" style={{ color: "var(--c-ink-soft)" }}>
              No funding sources yet — the payout draws from the account matching its currency. Provision a virtual
              account or wallet on the Account page to choose explicitly.
            </p>
          ) : (
            <select className="field" value={effectiveSourceValue} onChange={(e) => set("sourceValue", e.target.value)}>
              {sources.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          )}
          {crossCurrency && (
            <>
              <p className="mt-1 text-[12px] font-light" style={{ color: "var(--c-ink-soft)" }}>
                {source?.code} → {f.code}: you'll see a locked conversion quote before anything moves.
              </p>
              {insufficient && (
                <label className="mt-2 flex cursor-pointer items-start gap-2 text-[12px] font-light">
                  <input
                    type="checkbox"
                    className="mt-[2px]"
                    checked={f.autoExecute}
                    onChange={(e) => set("autoExecute", e.target.checked)}
                  />
                  <span>
                    {source?.code} balance is short ({fmtAmount(source?.available, source?.code)} available) — execute
                    automatically when the funds arrive.
                    <span style={{ color: "var(--c-ink-soft)" }}>
                      {" "}
                      The rate still locks at approval; Conduit runs the conversion the moment the balance covers it.
                    </span>
                  </span>
                </label>
              )}
            </>
          )}
        </div>

        <div>
          <label className="label">Recipient receives</label>
          <div className="flex gap-2">
            {(["fiat", "crypto"] as const).map((k) => (
              <button
                key={k}
                className="pill"
                style={
                  f.kind === k
                    ? { background: "var(--c-charcoal)", color: "var(--c-white)" }
                    : { boxShadow: "inset 0 0 0 1px var(--c-hairline)", color: "var(--c-ink-soft)" }
                }
                onClick={() => {
                  setRecipientChoice("new");
                  setF((x) => ({
                    ...x,
                    kind: k,
                    // A purpose the new kind doesn't offer must not survive the
                    // switch — a stale "prefunding" 422s on the crypto rail.
                    purpose: purposesFor(k).includes(x.purpose) ? x.purpose : "payment_for_goods_or_services",
                    code:
                      k === "crypto"
                        ? (STABLECOINS as readonly string[]).includes(x.code) ? x.code : "USDC"
                        : (FIAT as readonly string[]).includes(x.code) ? x.code : "USD",
                  }));
                }}
              >
                {k}
              </button>
            ))}
          </div>
        </div>

        {(recipientOptions.length > 0 || whitelistRecipients.length > 0) && (
          <div className="col-span-full">
            <label className="label">Recipient</label>
            <select className="field" value={recipientChoice} onChange={(e) => applyRecipient(e.target.value)}>
              <option value="new">New recipient — fill in manually</option>
              {recipientOptions.length > 0 && (
                <optgroup label="Your saved recipients">
                  {recipientOptions.map((r) => (
                    <option key={r.id} value={`saved:${r.id}`}>
                      {r.label}
                    </option>
                  ))}
                </optgroup>
              )}
              {f.kind === "fiat" && whitelistRecipients.length > 0 && (
                <optgroup label="Whitelisted (intercompany)">
                  {whitelistRecipients.map((wr) => (
                    <option key={wr.id} value={`wl:${wr.id}`} disabled={wr.status !== "registered"}>
                      {wr.legalName} {wr.status !== "registered" ? `(${wr.status.replaceAll("_", " ")})` : ""}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
            {recipientChoice.startsWith("wl:") && (
              <p className="mt-1 text-[12px] font-light" style={{ color: "var(--c-ink-soft)" }}>
                Purpose set to intercompany — this recipient is only valid for that purpose.
              </p>
            )}
          </div>
        )}

        <div>
          <label className="label">Purpose</label>
          <select className="field" value={f.purpose} onChange={(e) => set("purpose", e.target.value)}>
            {purposesFor(f.kind).map((p) => (
              <option key={p} value={p}>
                {PURPOSE_META[p]?.label ?? p.replaceAll("_", " ")}
              </option>
            ))}
          </select>
          {PURPOSE_META[f.purpose]?.hint && (
            <p className="mt-1 text-[12px] font-light" style={{ color: "var(--c-ink-soft)" }}>
              {PURPOSE_META[f.purpose].hint}
            </p>
          )}
          {f.purpose === "intercompany" && f.kind === "fiat" && (
            <p className="mt-1 text-[12px] font-light" style={{ color: "var(--c-ink-soft)" }}>
              Only goes to a{" "}
              <Link href="/payouts/whitelist" className="underline">
                whitelisted recipient
              </Link>
              .
            </p>
          )}
        </div>

        <div>
          <label className="label">Currency</label>
          <CurrencySelect codes={codes} value={f.code} onChange={(c) => set("code", c)} />
        </div>
        <div>
          <label className="label">Amount{crossCurrency ? ` (${f.code}, locked)` : ""}</label>
          <input className="field" inputMode="decimal" placeholder="0.00" value={f.amount} onChange={(e) => set("amount", e.target.value)} />
        </div>

        {f.kind === "crypto" ? (
          <>
            <div>
              <label className="label">Chain</label>
              <select className="field" value={f.chain} onChange={(e) => set("chain", e.target.value)}>
                {CHAINS.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Custody</label>
              <select className="field" value={f.custody} onChange={(e) => set("custody", e.target.value)}>
                <option value="third_party">third party</option>
                <option value="self">self custody</option>
              </select>
            </div>
            <div className="col-span-full">
              <label className="label">Destination address</label>
              <input className="field mono" placeholder="0x…" value={f.address} onChange={(e) => set("address", e.target.value)} />
            </div>
          </>
        ) : (
          <>
            <div>
              <label className="label">Destination country</label>
              <CountrySelect value={destCountry} onChange={setDestCountry} />
            </div>
            <div>
              <label className="label">Recipient type</label>
              <select className="field" value={f.recipientType} onChange={(e) => set("recipientType", e.target.value)}>
                <option value="business">business</option>
                <option value="individual">individual</option>
              </select>
            </div>
            <div className="col-span-full -mt-2">
              <span className="pill pill-cream">
                {destCountry === "US" ? "domestic · fedwire" : "international · swift"}
              </span>
              {reqLoading && (
                <span className="ml-2 text-[12px] font-light" style={{ color: "var(--c-ink-soft)" }}>
                  loading requirements…
                </span>
              )}
            </div>

            {grouped.map(([group, fields]) => (
              <div key={group} className="col-span-full grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <div className="eyebrow col-span-full mt-2" style={{ color: "var(--c-ink-soft)" }}>
                  {group}
                </div>
                {fields.map(renderReqField)}
              </div>
            ))}
          </>
        )}

        {canSaveRecipient && (
          <div className="col-span-full">
            <div className="card-cream flex flex-wrap items-center gap-2 p-4">
              <BookmarkPlus size={16} className="shrink-0" />
              <input
                className="field max-w-[220px]"
                placeholder="Name this recipient…"
                value={recipientLabel}
                onChange={(e) => setRecipientLabel(e.target.value)}
              />
              <button
                className="btn btn-dark !px-4 !py-1.5 text-[13px]"
                disabled={savingRecipient || !recipientLabel.trim()}
                onClick={saveRecipient}
              >
                {savingRecipient ? "Saving…" : "Save for reuse"}
              </button>
              {savedNotice && (
                <span className="text-[13px] font-light" style={{ color: "var(--c-ink-soft)" }}>
                  {savedNotice}
                </span>
              )}
            </div>
          </div>
        )}

        {needsDocs && (
          <div className="col-span-full">
            <div className="eyebrow mb-2 mt-2" style={{ color: "var(--c-ink-soft)" }}>
              supporting document
            </div>
            <p className="mb-2 text-[12px] font-light" style={{ color: "var(--c-ink-soft)" }}>
              {PURPOSE_META[f.purpose]?.docHint ? (
                <>For this purpose, {PURPOSE_META[f.purpose]!.docHint}.</>
              ) : (
                reqs?.documentation?.reason ?? "A supporting document is required unless the payout purpose is intercompany."
              )}{" "}
              <span style={{ opacity: 0.7 }}>
                Also accepted: {(reqs?.documentation?.acceptedDocumentTypes ?? ["invoice", "contract"]).join(", ").replaceAll("_", " ")}.
              </span>
            </p>
            <label
              className="flex cursor-pointer items-center gap-3 rounded-[16px] border border-dashed p-4"
              style={{ borderColor: "var(--c-hairline)" }}
            >
              <Upload size={16} />
              <span className="text-[14px] font-light">Upload PDF, JPG or PNG (max 10 MB)</span>
              <input type="file" multiple hidden accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => onUpload(e.target.files)} />
            </label>
            {docs.map((d) => (
              <div key={d.id} className="mt-2 flex items-center gap-2 text-[13px] font-light">
                <FileCheck size={14} /> {d.fileName}
                <span className="mono text-[11px]" style={{ color: "var(--c-ink-soft)" }}>
                  {d.id}
                </span>
              </div>
            ))}
          </div>
        )}

        <div className="col-span-full">
          <label className="label">Internal reference (optional)</label>
          <input className="field" value={f.clientReferenceId} onChange={(e) => set("clientReferenceId", e.target.value)} />
          <p className="mt-1 text-[11px] font-light" style={{ color: "var(--c-ink-soft)" }}>
            letters, numbers, and _ - : . only — anything else is converted to a dash
          </p>
        </div>
      </div>

      <button
        className="btn btn-primary mt-6 w-full"
        disabled={
          pending ||
          !f.amount ||
          !f.customerId ||
          !mayInitiate ||
          (f.kind === "fiat" && (reqLoading || missingRequired > 0)) ||
          (needsDocs && docs.length === 0)
        }
        onClick={submit}
      >
        {pending ? "Working…" : "Submit for approval"}
      </button>
      <p className="mt-2 text-center text-[12px] font-light" style={{ color: "var(--c-ink-soft)" }}>
        {!actingUser
          ? "select who you're acting as (top-left) — payouts are initiated under your team identity"
          : !mayInitiate
            ? `${actingUser.email} has the '${actingUser.role}' role and cannot initiate payouts`
            : f.kind === "fiat" && missingRequired > 0 && !reqLoading
              ? `${missingRequired} required field${missingRequired === 1 ? "" : "s"} remaining`
              : needsDocs && docs.length === 0
                ? "attach a supporting document to enable submitting"
                : crossCurrency
                  ? `initiating as ${actingUser.email}; the ${source?.code} → ${f.code} rate is locked at approval time`
                  : `initiating as ${actingUser.email} — a different teammate must approve before anything moves`}
      </p>

      {submittedId && (
        <div className="card-green mt-4 p-4 text-[14px] font-light">
          Submitted for approval (request #{submittedId}). A teammate other than {actingUser?.email ?? "the initiator"}
          must approve it on the Payouts page before anything is sent.
        </div>
      )}
      {error && (
        <div className="card-cream mt-4 p-4 text-[14px] font-light" style={{ color: "var(--c-danger)" }}>
          {error}
        </div>
      )}
    </div>
  );
}
